import logger from '@/services/logger'

export default {
  namespaced: true,
  state: {
    items: [],
    resellerUsers: [],
    resellerUsersTotal: 0,
    resellerUsersLoaded: false
  },
  getters: {
    getOrganizationById: (state) => (id) => {
      return state.items.find(org => org.id === id)
    }
  },
  actions: {
    async fetchOrganizationsList ({ dispatch }, { page = 1, limit = 10, sortBy = 'updated_at', order = 'desc' }) {
      try {
        const response = await dispatch('fetchDataWithParams', {
          stream: 'api/organization',
          params: { limit, page, sortBy, order }
        }, { root: true })

        // Transform response to match expected format
        // API returns { organizations: [...], totalResults: N } or { data: [...], totalResults: N }
        const organizationsData = response.organizations || response.data || []
        const organizations = Array.isArray(organizationsData) ? organizationsData : []

        return {
          organizations,
          totalResults: typeof response.totalResults === 'number' ? response.totalResults : 0,
          page,
          limit
        }
      } catch (error) {
        logger.error('Error fetching organizations list from API', {
          error: error.message,
          stack: error.stack
        })

        // Return empty result on error but don't throw to prevent UI crashes
        return {
          organizations: [],
          totalResults: 0,
          error: 'Unable to load organizations. Please try again or contact support.'
        }
      }
    },
    async searchOrganizations ({ dispatch }, { query, page = 1, limit = 10, sortBy = 'name', order = 'asc' }) {
      try {
        // Validate the search term. Query-string encoding is handled by fetchDataWithParams.
        if (!query || typeof query !== 'string') {
          logger.warn('Invalid search query provided', { query })
          return { organizations: [], totalResults: 0, page, limit }
        }

        const normalizedQuery = query.trim()
        if (!normalizedQuery) {
          logger.warn('Empty search query')
          return { organizations: [], totalResults: 0, page, limit }
        }

        // Valid sortBy values
        const validSortColumns = ['name', 'email', 'phone', 'address', 'created_at', 'updated_at']
        const safeSortBy = validSortColumns.includes(sortBy) ? sortBy : 'name'

        const response = await dispatch('fetchDataWithParams', {
          stream: 'api/organization/search',
          params: {
            query: normalizedQuery,
            limit,
            page,
            sortBy: safeSortBy,
            order: order.toUpperCase()
          }
        }, { root: true })

        const organizationsData = response.organizations || response.data || []
        const organizations = Array.isArray(organizationsData) ? organizationsData : []

        return {
          organizations,
          totalResults: typeof response.totalResults === 'number' ? response.totalResults : 0,
          page,
          limit
        }
      } catch (error) {
        logger.error('Error searching organizations from API', {
          error: error.message,
          stack: error.stack,
          query
        })

        return {
          organizations: [],
          totalResults: 0,
          page,
          limit,
          error: 'Unable to search organizations. Please try again or contact support.'
        }
      }
    },
    async createOrganization ({ dispatch }, organizationData) {
      try {
        const response = await dispatch('postData', {
          stream: 'api/organizations',
          data: organizationData,
          headers: { Platform: 'BITS_ADMIN' }
        }, { root: true })

        return response
      } catch (error) {
        logger.error('Error creating organization', {
          error: error.message,
          stack: error.stack
        })
        throw error
      }
    },
    async updateOrganization ({ dispatch }, { organizationId, organizationData }) {
      try {
        const response = await dispatch('putData', {
          stream: `api/organization/${encodeURIComponent(organizationId)}`,
          data: organizationData,
          headers: { Platform: 'BITS_ADMIN' }
        }, { root: true })

        return response
      } catch (error) {
        logger.error('Error updating organization', {
          error: error.message,
          stack: error.stack
        })
        throw error
      }
    },
    async deleteOrganization ({ dispatch }, organizationId) {
      try {
        const response = await dispatch('deleteData', {
          stream: `api/organization/${encodeURIComponent(organizationId)}`
        }, { root: true })

        return response
      } catch (error) {
        logger.error('Error deleting organization', {
          error: error.message,
          stack: error.stack
        })
        throw error
      }
    },
    async fetchOrganizationSubscription ({ dispatch }, organizationId) {
      try {
        if (!organizationId || typeof organizationId !== 'string') {
          logger.warn('Invalid organization ID for subscription lookup', { organizationId })
          return {
            organizationId: '',
            licensedSeats: 0,
            totalMembers: 0,
            isOverLimit: false,
            lastUpdatedAt: null,
            activeUserDefinition: 'Active users are users with active=true or status=active/enabled.',
            raw: {}
          }
        }

        const encodedOrganizationId = encodeURIComponent(organizationId)
        const response = await dispatch('fetchData', {
          stream: `organizations/${encodedOrganizationId}/subscription?include_upcoming=true`
        }, { root: true })

        const payload = response && typeof response === 'object'
          ? (response.data && typeof response.data === 'object' ? response.data : response)
          : {}

        const parseNumber = (value) => {
          if (typeof value === 'number' && Number.isFinite(value)) return value
          if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
            return Number(value)
          }
          return null
        }

        const resolveLicensedSeats = (subscriptionPayload) => {
          const seatCandidates = [
            subscriptionPayload.licensedSeats,
            subscriptionPayload.licensedSeatCount,
            subscriptionPayload.seatCount,
            subscriptionPayload.maxSeats,
            subscriptionPayload.subscription?.licensedSeats,
            subscriptionPayload.subscription?.seatCount,
            subscriptionPayload.subscription?.maxSeats,
            subscriptionPayload.plan?.licensedSeats,
            subscriptionPayload.plan?.seatCount
          ]

          for (const candidate of seatCandidates) {
            const parsed = parseNumber(candidate)
            if (parsed !== null) {
              return parsed
            }
          }

          return 0
        }

        const licensedSeats = resolveLicensedSeats(payload)
        const totalMembers = parseNumber(payload.total_members) ?? 0
        const activeMembers = parseNumber(payload.active_members) ?? 0
        const isOverLimit = activeMembers > licensedSeats
        const transactionsUsed = parseNumber(payload.usedTransactions) ?? parseNumber(payload.subscription?.usedTransactions) ?? 0

        return {
          organizationId,
          licensedSeats,
          totalMembers,
          activeMembers,
          transactionsUsed,
          subscription: payload.subscription && typeof payload.subscription === 'object' ? payload.subscription : {},
          upcomingSubscription: payload.upcomingSubscription || payload.upcoming_subscription || null,
          isOverLimit,
          lastUpdatedAt: payload.updatedAt || payload.lastUpdatedAt || payload.subscription?.updatedAt || payload.subscription?.createdAt || new Date().toISOString(),
          activeUserDefinition: 'Active users are users with active=true or status=active/enabled.',
          raw: payload
        }
      } catch (error) {
        logger.error('Error fetching organization subscription details', {
          error: error.message,
          stack: error.stack,
          organizationId
        })

        return {
          organizationId,
          licensedSeats: 0,
          totalMembers: 0,
          activeMembers: 0,
          transactionsUsed: 0,
          subscription: {},
          isOverLimit: false,
          lastUpdatedAt: null,
          activeUserDefinition: 'Active users are users with active=true or status=active/enabled.',
          error: 'Unable to load organization subscription details. Please try again later.',
          raw: {}
        }
      }
    },

    async createSubscription ({ dispatch }, subscriptionData) {
      try {
        const response = await dispatch('createData', {
          stream: 'api/organizations/subscription',
          body: subscriptionData
        }, { root: true })

        return response
      } catch (error) {
        logger.error('Error creating subscritption', {
          error: error.message,
          stack: error.stack
        })
        throw error
      }
    },
    async upgradeSubscription ({ dispatch }, { subscriptionId, planType, maxSeats }) {
      return dispatch('createData', {
        stream: `api/organizations/subscription/${encodeURIComponent(subscriptionId)}/upgrade`,
        body: { planType, maxSeats }
      }, { root: true })
    },
    async downgradeSubscription ({ dispatch }, { subscriptionId, planType, maxSeats }) {
      return dispatch('createData', {
        stream: `api/organizations/subscription/${encodeURIComponent(subscriptionId)}/downgrade`,
        body: { planType, maxSeats }
      }, { root: true })
    },
    async fetchResellerUsers ({ dispatch, state, commit }, { page = 1, limit = 100, forceRefresh = false } = {}) {
      // Return the cached reseller list when it was already loaded once.
      // The cache lives in memory and is naturally cleared on page reload.
      if (!forceRefresh && state.resellerUsersLoaded) {
        return {
          resellers: state.resellerUsers,
          totalResults: state.resellerUsersTotal
        }
      }

      try {
        const response = await dispatch('fetchDataWithParams', {
          stream: 'api/organization/resellers',
          params: { limit, page }
        }, { root: true })

        // API returns { data: [...], totalResults: N }
        const resellersData = response.data || []
        const resellers = Array.isArray(resellersData) ? resellersData : []
        const totalResults = typeof response.totalResults === 'number' ? response.totalResults : resellers.length

        commit('setResellerUsers', { resellers, totalResults })

        return {
          resellers,
          totalResults
        }
      } catch (error) {
        logger.error('Error fetching reseller users from API', {
          error: error.message,
          stack: error.stack
        })

        return {
          resellers: [],
          totalResults: 0,
          error: 'Unable to load reseller users. Please try again or contact support.'
        }
      }
    },
    async transferOrganization ({ dispatch }, { organizationId, resellerUserId }) {
      try {
        if (!organizationId || typeof organizationId !== 'string') {
          logger.warn('Invalid organization ID for transfer', { organizationId })
          return { success: false, message: 'Invalid organization ID' }
        }

        const response = await dispatch('createData', {
          stream: `api/organization/${encodeURIComponent(organizationId)}/transfer`,
          body: { resellerUserId: resellerUserId || null },
          headers: { Platform: 'BITS_ADMIN' }
        }, { root: true })

        return response
      } catch (error) {
        logger.error('Error transferring organization', {
          error: error.message,
          stack: error.stack,
          organizationId
        })
        throw error
      }
    }
  },
  mutations: {
    setOrganizations (state, organizations) {
      state.items = organizations
    },
    addOrganization (state, organization) {
      state.items.push(organization)
    },
    updateOrganization (state, updatedOrganization) {
      const index = state.items.findIndex(org => org.id === updatedOrganization.id)
      if (index !== -1) {
        state.items.splice(index, 1, updatedOrganization)
      }
    },
    removeOrganization (state, organizationId) {
      state.items = state.items.filter(org => org.id !== organizationId)
    },
    setResellerUsers (state, { resellers, totalResults }) {
      state.resellerUsers = resellers
      state.resellerUsersTotal = totalResults
      state.resellerUsersLoaded = true
    }
  }
}
